/**
 * Single source of truth for status enums + display labels (G2/G3, M2 client
 * rebuild): booking / payment / inquiry statuses, booking-type labels, and the
 * send channel/delivery-status labels. Exposed to the kiosk client as the
 * `statuses` block on `GET /api/service-descriptors` — the client renders
 * labels from this registry and never hardcodes an enumeration (kills the
 * app.js:82-90 class).
 *
 * Enum VALUES mirror the migration enums verbatim (migrations.ts: booking_status,
 * payment_status, inquiry_status) and send-schema.ts (SEND_STATUSES/CHANNELS).
 * Labels match the incumbent client's maps where the e2e net asserts on them
 * ('Quoted' is asserted by bookings-view.spec.ts — incumbent parity).
 *
 * Imports only sibling shared modules (shared <- engine <- server direction).
 */
import { CUSTOMER_SERVICE_LABELS, SERVICE_LABELS } from './service-schema.js';
import { CHANNELS, SEND_STATUSES, type Channel, type SendOutcome, type SendStatus } from './send-schema.js';

export const BOOKING_STATUSES = ['new', 'quoted', 'confirmed', 'cancelled', 'completed'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/**
 * ⭐ `completed` READS `Travelled` SINCE T1749 M3, AND THE WORD IS THE RULING
 * (1749 D7, the sitting of 2026-09-10, Words 3). `Completed` is an office word
 * for a fact the customer experiences as a journey that happened; `Travelled` is
 * the word the nurse's step line has used since T1723 M6, so this is the
 * operator surfaces adopting a SHIPPED token rather than a new word being minted.
 * ⚠️ THE ENUM VALUE IS UNTOUCHED. `completed` is `booking_status` in the
 * migration enums; this changes what a human READS, never what the column holds.
 * ⚠️ AND `Quoted` STAYS, deliberately: it is retail's word for a priced booking
 * and `e2e/tests/green/bookings-view.spec.ts:41` grades it. Words 3 re-ruled the
 * NURSE's request vocabulary and the last booking stage — not the whole ladder.
 * ⛔ THIS MAP IS COPIED IN `packages/ui/src/status-presentation.ts` as
 * `BOOKING_LABELS` (that package cannot import from this tool), and
 * `quote-helper/test/client/status-presentation.spec.ts` asserts the two are
 * EQUAL key for key. Edit one and the other reds; there is no third copy.
 */

export const BOOKING_STATUS_LABELS: Readonly<Record<BookingStatus, string>> = {
  new: 'New',
  quoted: 'Quoted',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Travelled',
};

/**
 * ⚠️ MOVED BELOW `BOOKING_STATUS_LABELS` AT THE M8 REVIEW WAVE. It was first
 * inserted directly ABOVE that const — which put it BETWEEN the map and the
 * docblock describing it, so the paragraph carrying the `packages/ui` mirror-parity
 * rule ("Edit one and the other reds; there is no third copy") floated above a
 * different symbol entirely. A docblock that no longer hovers on what it describes
 * is how the next reader inherits the wrong rule.
 */
/**
 * ⭐⭐ CAN THIS RUN STILL BE CANCELLED? ONE EXPRESSION, TWO SCREENS (T2088 M8).
 *
 * Until M8 this predicate existed ONCE, inline, at `BookingDetail.tsx`'s
 * `cancellable` prop. R-4 puts a second cancel control on Helen's portal
 * Requests row, and two hand-written copies of a MONEY-BEARING visibility rule
 * is the shape where one screen keeps offering a control the other has retired
 * — so the rule moved here rather than being typed out again.
 *
 * ⛔ IT IS THE SCREEN'S RULE, NOT THE SERVER'S, AND THE SERVER IS STILL THE
 * AUTHORITY. `core/cancel-booking.ts` refuses inside its own transaction on a
 * row it holds a `FOR UPDATE` lock over — `'cancelled'` returns
 * `{ changed: false }` and `'completed'` throws — and it also refuses things
 * this predicate cannot see at all (an issued account invoice, a paid deposit).
 * This only decides whether to OFFER the control; every caller must still
 * handle a refusal by name.
 *
 * ⚠️ IT TAKES A `string`, not `BookingStatus`, because both call sites read a
 * status off the wire: `PortalCrossing.status` is typed `string | null` (the raw
 * `bookings.status` carried across the seam). An unrecognised value therefore
 * reads as CANCELLABLE, which is the direction that fails toward a refusal the
 * operator can see rather than toward a control that silently disappears.
 *
 * ⛔ IT IS NOT MIRRORED INTO `packages/ui`, unlike `BOOKING_STATUS_LABELS`
 * below: that copy exists because a shared PRESENTATION component needs the
 * words. This is a business rule, both readers are in this tool, and a second
 * copy would be the exact drift it was extracted to prevent.
 */
export function isBookingCancellable(status: string | null | undefined): boolean {
  return status !== 'cancelled' && status !== 'completed';
}

/**
 * ⭐ T1155 (Alex, q304) - A SETTLED CANCELLATION: a cancelled job whose refund is
 * neither unconfirmed nor failed. It stays out of live work (the Bookings All tab,
 * and from board 2812 M1 the server's From today and Tomorrow lists) and is still
 * listed under Cancelled and in History. The ONE copy of the ruling, read by
 * the Bookings screen (`client/screens/Bookings.tsx`) and by `server/todo-piles.ts`.
 * ⛔ AN ALLOW-LIST OF THE SETTLED REFUND STATES (board 2812 M3, the M1 security
 * Low): only `none`, `refunded` and `part_refunded` are settled. A refund state
 * added later, or any value this build does not know, is NOT settled, so its row
 * stays VISIBLE in live work: the fail-safe direction on a screen whose job is not
 * to hide money. (The two-`!==` form this replaced did the opposite: it read every
 * unknown token as settled and hid it.) `refund_state` is the list row's own
 * (`summariseBookingRefundState`); `unconfirmed` and `failed` are the two Alex
 * ruled stay in live work.
 */
export const SETTLED_REFUND_STATES: readonly string[] = ['none', 'refunded', 'part_refunded'];

export function isSettledCancellation(r: { status: string; refund_state: string }): boolean {
  return r.status === 'cancelled' && SETTLED_REFUND_STATES.includes(r.refund_state);
}

/**
 * ⭐⭐ board 2814 M8R1 (Captain's ruling on Q287; fix round 1, CR-2) - THE ONE
 * PREDICATE FOR "THIS LINKED RUN KEEPS ITS REQUEST OPEN": it has a `travelled`
 * event and its booking is not `cancelled`. Read by the server's refusal
 * (`travelledRunRefusal`, 409 `request_run_travelled`) AND by both screens' confirms
 * (the Requests row, booking detail's ask block), so a confirm never offers a "Yes"
 * the server will refuse, nor withholds one it would take.
 * ⛔ DELIBERATELY STRICTER THAN `isBookingCancellable`: a `completed` run that
 * travelled is still on the month's bill, so closing its request would show the home
 * "Cancelled" for a billed trip, which is the case the ruling exists to stop. Only a
 * CANCELLED booking (booking detail's acknowledged cancel took it off the bill) lets
 * the request close.
 */
export function travelledRunKeepsRequest(status: string | null | undefined, travelled: boolean | undefined): boolean {
  return travelled === true && status !== 'cancelled';
}

export const PAYMENT_STATUSES = [
  'pending',
  'deposit_sent',
  'deposit_paid',
  'balance_due',
  'paid',
  'refunded',
  'failed',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_STATUS_LABELS: Readonly<Record<PaymentStatus, string>> = {
  pending: 'Pending',
  deposit_sent: 'Deposit sent',
  deposit_paid: 'Deposit paid',
  balance_due: 'Balance due',
  paid: 'Paid',
  refunded: 'Refunded',
  failed: 'Failed',
};

export const INQUIRY_STATUSES = ['new', 'normalized', 'dismissed', 'spam'] as const;
export type InquiryStatusValue = (typeof INQUIRY_STATUSES)[number];

export const INQUIRY_STATUS_LABELS: Readonly<Record<InquiryStatusValue, string>> = {
  new: 'New',
  normalized: 'Quoting',
  dismissed: 'Dismissed',
  spam: 'Spam',
};

/** booking_type mirrors the service ids plus 'recurring_contract' (Round 14). */
export const BOOKING_TYPE_LABELS: Readonly<Record<string, string>> = {
  ...SERVICE_LABELS,
  recurring_contract: 'Recurring',
};

/**
 * What a CUSTOMER reads in place of a service label when there is none.
 *
 * T549 M3 review. The customer sinks used to end `?? type`, which is the very
 * defect M3 removes - it only moved the raw internal token one step further
 * out. A generic noun is always true and always readable; the OPERATOR alert
 * keeps the raw token on purpose (see bookingTypeLabel below).
 */
export const GENERIC_SERVICE_LABEL = 'your booking';

/**
 * The human label for a stored `booking_type`, or null when there is none.
 *
 * `Object.hasOwn`, not a bare index - the same reason `contactPreferenceLabel`
 * in send-schema.ts does it. BOOKING_TYPE_LABELS is a plain object literal, so
 * `BOOKING_TYPE_LABELS['__proto__']` returns Object.prototype and
 * `['constructor']` returns the Object function. Both are TRUTHY, so a `?? x`
 * tail can never fire and the message renders '[object Object]' or the source
 * text of a native function. Not reachable through the booking_type DB enum
 * today, but this map now feeds three message paths and the guard costs a call.
 *
 * Returns null rather than a fallback so every caller must STATE its fallback:
 * the operator alert wants the raw token (a raw value beats a hole on a lock
 * screen), a customer must never see one. Note the client store has its own
 * same-named helper for the SERIALISED registry (client/store/registry.ts) -
 * different object, different layer.
 */
export function bookingTypeLabel(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  return Object.hasOwn(BOOKING_TYPE_LABELS, v) ? BOOKING_TYPE_LABELS[v]! : null;
}

/**
 * The label for a stored `booking_type` ON THE CUSTOMER PLANE, or null when
 * there is none (T1679, board #1679).
 *
 * WHY IT IS A SECOND FUNCTION rather than a change to the map above: the two
 * planes genuinely disagree for one service. `local-other` is "Custom / Other"
 * to Helen - the right name for a catch-all bucket on her screen - and a real
 * customer received "Your quote for Custom / Other is EUR360". Editing
 * `SERVICE_LABELS` would have fixed the email and broken the operator vocabulary
 * with it: the deposit-paid and deposit-failed alerts, the dispatcher, the kiosk
 * UI and the public form's own service picker all read that map, and 18 specs
 * pin the string.
 *
 * ⛔ IT DELEGATES, IT DOES NOT INDEX. `bookingTypeLabel` carries the
 * `Object.hasOwn` guard, and re-implementing the lookup here would drop it:
 * `BOOKING_TYPE_LABELS['__proto__']` is truthy, so no `??` tail could fire and
 * the customer would be sent '[object Object]'. Delegating inherits the guard
 * and cannot drift from it. The `CUSTOMER_SERVICE_LABELS` lookup below is
 * guarded the same way and for the same reason, even though that map is
 * `Partial` and currently holds one key.
 *
 * ⚠️ RETURNS NULL, NEVER A FALLBACK - the same contract as `bookingTypeLabel`
 * and for the same reason: each caller states its own. All three customer sites
 * keep their `?? GENERIC_SERVICE_LABEL` tail. `recurring_contract` has no
 * service descriptor at all (it exists only in the map above), so it resolves
 * through the delegation rather than needing an entry of its own.
 */
export function customerServiceLabel(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  if (Object.hasOwn(CUSTOMER_SERVICE_LABELS, v)) {
    const label = CUSTOMER_SERVICE_LABELS[v as keyof typeof CUSTOMER_SERVICE_LABELS];
    if (typeof label === 'string' && label.trim() !== '') return label;
  }
  return bookingTypeLabel(v);
}

/** Operator-facing labels for the send channels (G3). */
export const CHANNEL_LABELS: Readonly<Record<Channel, string>> = {
  sms: 'Text message',
  whatsapp: 'WhatsApp',
  // T549 M1: the Record<Channel, string> type is what forced this entry the
  // moment 'email' joined CHANNELS — that is the registry doing its job.
  email: 'Email',
  // T660 M1: what Helen actually did — a deposit URL was put on her clipboard.
  // Says the thing that happened and claims no transmission; the delivery word
  // beside it stays 'Copied' and the outcome stays 'Not confirmed'.
  link: 'Payment link',
  // T1988: the deposit-paid alert to Owen and Helen by email. Says who it went
  // to; the timeline kind beside it reads 'Team alert sent'.
  team_email: 'Team email',
};

/** Operator-facing labels for send_ledger delivery statuses (G3). Honest words,
 *  no em dashes: 'send_failed' is our bookend for "the transport call errored". */
export const SEND_STATUS_LABELS: Readonly<Record<SendStatus, string>> = {
  composed: 'Composed',
  accepted: 'Accepted',
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  undelivered: 'Not delivered',
  failed: 'Failed',
  send_failed: 'Not sent',
  // T524: Helen copied the message to paste it herself. Says what happened and
  // claims nothing about the customer receiving it — see STATUS_COPIED.
  copied: 'Copied',
};

/**
 * THE per-booking timeline vocabulary (T608 M4, R6d) and its operator wording.
 *
 * The timeline is a READ-TIME UNION of three tables that already exist -
 * booking_events, send_ledger and payments (core/data/bookings-repo.ts
 * getBookingTimeline) - so this list is not a new persisted enum: it is the
 * closed set of FACTS that union can report, and every member maps to exactly
 * one (source, row-shape) pair. Nothing writes these values anywhere.
 *
 * Why the vocabulary lives in shared/ rather than beside the union in core/:
 * `Record<BookingTimelineKind, string>` below is what makes a new kind
 * impossible to add WITHOUT operator wording (the same mechanism CHANNEL_LABELS
 * praises), and core/ cannot own the label map - shared/ may not import core/.
 * It also keeps the client one import from the wording, with no registry
 * plumbing for a map that is compile-time constant.
 *
 * The three send kinds are one table read three ways, and the split is a
 * correctness requirement rather than polish: send_ledger holds quote sends
 * (quote_id set), the customer's deposit-received confirmation
 * (notify/deposit-received-notify.ts) and the OPERATOR deposit-paid alerts, one
 * row per recipient (notify/deposit-paid-notify.ts). Labelling all three "Quote
 * sent" would tell Helen a quote went out when what actually happened was that
 * Owen got a WhatsApp - the same one-label-for-two-facts defect that
 * getBookingDetail's `latest_send` discriminator already exists to avoid.
 *
 * 'deposit_paid' is spelled to match the PAYMENT_STATUSES member above on
 * purpose: it is the same fact, so the timeline renders it with
 * PAYMENT_STATUS_LABELS and cannot drift from the chip beside it.
 */
export const BOOKING_TIMELINE_KINDS = [
  // booking_events arm - the M3a event kinds, passed through unchanged.
  'inquiry_normalised',
  'status_changed',
  'deposit_link_created',
  // send_ledger arm.
  'quote_sent',
  'customer_message_sent',
  // ⭐ board 2811 rework part 2c (Captain item 15): a balance-link text, told from
  // the deposit-received text by its ledger sentinel (`listSendsForTimeline`), so
  // the two no longer read as two identical "Customer message sent" rows.
  'balance_text_sent',
  'team_alert_sent',
  // T524: the manual-copy row. A FOURTH send_ledger reading, and it needs its
  // own kind rather than falling through to one of the three above: a copy row
  // carries channel 'whatsapp' with a NULL quote_id, which the pre-T524 mapping
  // read as 'team_alert_sent' — telling Helen Owen got a WhatsApp when what
  // actually happened was that she pressed a copy button.
  'quote_copied',
  // T1033 M7: the run happened. Given operator wording deliberately — unlike
  // 'invoiced' and 'account_linked', which are audit-only — because ADR4-D11
  // puts Travelled on the spine BOTH roles see, so it has to be legible.
  'travelled',
  // payments arm.
  'deposit_paid',
  // T820 M5: a delayed payment that failed. Must be listed HERE as well as in
  // BOOKING_EVENT_KINDS — getBookingTimeline silently SKIPS a row whose kind is
  // not a timeline kind, so omitting it would persist the record and render
  // nothing, which is indistinguishable from never having written it.
  'deposit_payment_failed',
  /**
   * ⭐⭐ T1717 M6 (DD6, P0-11). Given operator wording deliberately, unlike
   * 'invoiced' and 'account_linked', which stay audit-only: the WHOLE POINT of
   * this row is that an override of the home's answer is VISIBLE. An audit row
   * nobody can see would leave the override exactly as silent as it was before
   * this milestone, which is the defect board #1705 was raised for.
   * ⛔ LISTED HERE AS WELL AS IN BOOKING_EVENT_KINDS, because getBookingTimeline
   * silently SKIPS a row whose kind is not a timeline kind - omitting it would
   * persist the record and render nothing, indistinguishable from never having
   * written it (the same trap 'deposit_payment_failed' above records).
   */
  'return_answer_overridden',
  /**
   * ⭐⭐ T1746 M4. AUDIT-ONLY UNTIL NOW, AND THIS ENTRY IS THE COPY DECISION
   * ITS OWN EXCLUSION NOTE DEFERRED ("a copy decision for the screen that wants
   * it (M8)", core/data/booking-events-repo.ts). T1746 M4 is that screen.
   *
   * ⛔ BECAUSE THE ROW NOW CARRIES A REASON SOMEBODY HAS TO READ. Captain's M3
   * gate delta 7a asked for a VISIBLE account of why a generated occurrence is
   * unpriced, and M4's first commit put it in this event's `detail`
   * (`series N, unpriced: no account price for this destination`). With the kind
   * excluded here, `getBookingTimeline` SKIPPED the row and the booking read
   * "No history recorded" - so the reason was written, stored, and invisible.
   * A row nobody can see is indistinguishable from one that was never written,
   * which is the exact trap 'deposit_payment_failed' and
   * 'return_answer_overridden' above already record.
   *
   * ⛔ AND IT IS THE ONLY ACCOUNT OF ITSELF A GENERATED RUN HAS. Every other
   * booking on this screen was captured by a person; this one was written by a
   * machine onto a customer's account with no human in the loop, and the
   * operator opening it is usually doing so BECAUSE the price line reads
   * "No agreed price".
   *
   * ⚠️ 'occurrence_withdrawn' DELIBERATELY STAYS AUDIT-ONLY. Its withdrawal is
   * already visible on this same timeline through the `status_changed` row the
   * generator files beside it, so a second rendered line would say the same
   * thing twice.
   */
  'occurrence_generated',
  /**
   * ⭐ board 2249 M3: the whole-job documents Helen issues. LISTED HERE AS WELL
   * AS IN BOOKING_EVENT_KINDS for 'deposit_payment_failed''s reason: the
   * timeline SKIPS a kind it does not list, and no other source renders these
   * documents (the timeline reads payments, booking_events and send_ledger, never
   * balance_invoices). The row's `detail` is ids and booleans only.
   */
  'job_invoice_issued',
  'job_invoice_replaced',
  'job_receipt_issued',
  /**
   * ⭐ board 2811 M2: the scheduled balance ask (an email, or since M6 a text)
   * was moved with the journey, or withdrawn (core/balance-ask-reconcile.ts).
   * LISTED HERE AS WELL AS IN
   * BOOKING_EVENT_KINDS for 'deposit_payment_failed''s reason: the timeline SKIPS
   * a kind it does not list, and no other row renders either fact.
   */
  'balance_ask_moved',
  'balance_ask_withdrawn',
  // board 2788 M1 (DD-6): the date and time moved. Listed here as well as in
  // BOOKING_EVENT_KINDS for 'deposit_payment_failed''s reason: the timeline SKIPS
  // a kind it does not list, and no other source records the move.
  'date_changed',
  // ⭐ board 2814 M7 (DD-7): the run was passed to a taxi firm, and the undo.
  // Listed here for 'deposit_payment_failed''s reason. The row's detail is a fixed
  // token: the note is never in an event.
  'taxi_passed',
  'taxi_pass_undone',
  // ⭐ board 2814 M8R1 (R5): Helen changed the run's price. Listed here for
  // 'deposit_payment_failed''s reason; the detail is the two amounts in cents.
  'price_changed',
] as const;
export type BookingTimelineKind = (typeof BOOKING_TIMELINE_KINDS)[number];

export const BOOKING_TIMELINE_KIND_LABELS: Readonly<Record<BookingTimelineKind, string>> = {
  inquiry_normalised: 'Inquiry received',
  travelled: 'Run travelled',
  status_changed: 'Status changed',
  deposit_link_created: 'Deposit link created',
  quote_sent: 'Quote sent',
  customer_message_sent: 'Customer message sent',
  // Contains no other label and is contained by none (the `getByText` rule below);
  // the screen's "Balance texted on ..." sentence is not a label and does not match.
  balance_text_sent: 'Balance text sent',
  team_alert_sent: 'Team alert sent',
  // T524. Past tense of what Helen did, and nothing about what the customer
  // got — the outcome word beside it stays 'Not confirmed'.
  quote_copied: 'Quote copied',
  // Same words as PAYMENT_STATUS_LABELS.deposit_paid, deliberately: see above.
  deposit_paid: PAYMENT_STATUS_LABELS.deposit_paid,
  // T820 M5. A string Helen reads, so it was specified in the plan rather than
  // left to acquire a default. NOT borrowed from PAYMENT_STATUS_LABELS: this path
  // deliberately does NOT write payments.status, so there is no payment status
  // named 'failed' for it to mirror, and implying one would be a lie about the row.
  deposit_payment_failed: 'Deposit payment failed',
  /**
   * ⭐ T1717 M6. "by ECMB" is load-bearing and is not decoration: this timeline
   * is read when a home queries a bill, and the sentence has to say WHOSE change
   * it was. "Return answer changed" alone would read as the home changing its
   * mind, which is the opposite of what the row records. The actor column names
   * the individual; the label names the side.
   */
  return_answer_overridden: 'Return answer changed by ECMB',
  /**
   * ⭐ T1746 M4. "from the series" is the load-bearing half: it says a MACHINE
   * wrote this booking off a recurring contract, which is the one fact that
   * distinguishes it from every other row on the screen. The series id and the
   * unpriced reason ride in `detail`, which the row renders verbatim after this
   * label, so the wording deliberately does not repeat either.
   * ⚠️ `Record<BookingTimelineKind, string>` is what forces this entry to exist:
   * adding the kind above without a label is a compile error, never a silent
   * blank on Helen's screen.
   */
  occurrence_generated: 'Generated from the series',
  // board 2249 M3. What Helen did. ⚠️ No label contains another, nor an existing
  // one such as 'Paid' (the TIMELINE_SEND_OUTCOME_LABELS rule): e2e's
  // case-insensitive `getByText` would otherwise match two rows at once.
  job_invoice_issued: 'Whole-job invoice issued',
  job_invoice_replaced: 'Whole-job invoice replaced',
  job_receipt_issued: 'Receipt issued',
  // board 2811 M2 (plan step 4). The same no-containment rule as the three above.
  // ⭐ board 2811 M8: CHANNEL-NEUTRAL, as A3 L6 made the withdrawn label. A reschedule
  // moves a scheduled TEXT ask too, and "email" would be false on that row.
  balance_ask_moved: 'Balance ask moved',
  balance_ask_withdrawn: 'Balance ask withdrawn',
  // board 2788 M1. ⚠️ Contains no other label and is contained by none (the
  // case-insensitive `getByText` rule above); 'Status changed' is the near one.
  date_changed: 'Date and time changed',
  // ⭐ board 2814 M7 (DD-7). Each contains no other label and is contained by none.
  taxi_passed: 'Run passed to a taxi',
  taxi_pass_undone: 'Taxi pass undone',
  // ⭐ board 2814 M8R1. Contains no other label and is contained by none.
  price_changed: 'Price changed',
};

/**
 * Operator wording for a timeline kind, or null when there is none.
 *
 * `Object.hasOwn`, not a bare index - same reason as bookingTypeLabel above:
 * '__proto__' and 'constructor' walk the prototype and return TRUTHY non-strings,
 * so a `?? fallback` tail could never fire. The kind arrives over HTTP on the
 * client side of this map, which is exactly where that matters.
 */
export function bookingTimelineKindLabel(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  return Object.hasOwn(BOOKING_TIMELINE_KIND_LABELS, v)
    ? BOOKING_TIMELINE_KIND_LABELS[v as BookingTimelineKind]
    : null;
}

/**
 * WHAT A SEND LINE ON THE TIMELINE SAYS ABOUT THE CUSTOMER (T608 M4 review,
 * finding 3) - the timeline's OWN wording, keyed on the shared classifier's
 * `outcome` and never on the delivery status alone.
 *
 * WHY THIS MAP EXISTS AT ALL. The timeline used to word its send chip with
 * SEND_STATUS_LABELS, which is `Record<SendStatus, string>` - keyed on the status
 * with no channel in sight. `accepted` means opposite things on the two channels
 * (shared/send-schema.ts sendOutcome: terminal success on email, Twilio's
 * EARLIEST lifecycle state on SMS), so both rows read the one word 'Accepted' and
 * differed only by chip TONE. That is the M4 plan's per-channel requirement unmet
 * at the word level, and a colour-only distinction is a WCAG 1.4.1 failure - the
 * fact does not exist for an operator who cannot see the two tones apart.
 *
 * WHY NOT FIX SEND_STATUS_LABELS. It cannot be fixed there: it is keyed on status
 * alone by type, it is shared with the send surface, and 'Composed' is pinned by
 * an M3b spec. So the CHANNEL-dependent reading lives here, one layer out, and the
 * shared status registry is untouched.
 *
 *  - delivered: 'Delivered'. Faithful to the SERVED outcome, which is the whole
 *    contract - sendOutcome declares email-'accepted' to mean "it demonstrably
 *    left us for them", and this map's job is to word that answer, not to
 *    second-guess the classifier.
 *  - sent: 'Sent, no receipt'. Handed to the carrier with nothing confirming
 *    arrival. It must read as NEITHER good news nor failure: a failure word
 *    invites the same re-send harm described for 'unknown' below, and a
 *    delivery word is the board-1962 defect itself.
 *    ⛔ NO LABEL IN THIS MAP MAY CONTAIN ANOTHER, CASE-INSENSITIVELY, and this
 *    arm is where that first bit. 'Sent, not confirmed' was the obvious wording
 *    and is unusable: it contains 'Not confirmed'. Measured - vitest `toContain`
 *    is case-SENSITIVE so the unit pin survives, but Playwright `getByText`
 *    defaults to case-INSENSITIVE substring, so `getByText('Not confirmed')`
 *    in e2e would match this row too and raise a strict-mode violation the
 *    moment both render together. The suite's set-size distinctness check
 *    (send-schema.spec.ts) cannot see containment; the pairwise check added
 *    alongside it can.
 *  - failed: 'Not delivered'. True for all three failed statuses (a carrier
 *    bounce, a hard failure, and our own send_failed bookend) without claiming
 *    which - the error_code on the same line is what distinguishes them.
 *  - unknown: 'Not confirmed'. Neither sent nor failed, deliberately. This arm
 *    carries in-flight SMS AND 'composed', which means "we were about to send
 *    this" for a message the customer may well have received (including a live
 *    payable deposit link), so it must not read as failure - a word like "Not
 *    sent" would invite Helen to re-send a quote and re-mint a link the customer
 *    already holds.
 *
 * simplified: the raw provider status word (Queued / Sent / Accepted / Not sent)
 * no longer appears on the timeline line. Upgrade trigger: Helen needs to tell a
 * carrier bounce from a transport error at a glance rather than from error_code.
 */
export const TIMELINE_SEND_OUTCOME_LABELS: Readonly<Record<SendOutcome, string>> = {
  delivered: 'Delivered',
  sent: 'Sent, no receipt',
  failed: 'Not delivered',
  unknown: 'Not confirmed',
};

/**
 * Operator wording for a served send `outcome`. TOTAL, unlike its siblings above:
 * every other label helper returns null so each caller must state its own
 * fallback, but here there is exactly one right answer for "no classification" -
 * the honest-uncertainty word - and it matches what the timeline's chip tone
 * already does with an unrecognised outcome. A null return would only invite a
 * caller to invent a cheerier default.
 *
 * `Object.hasOwn`, not a bare index: the outcome arrives over HTTP on the client
 * side of this map, so '__proto__' / 'constructor' would otherwise walk the
 * prototype and return a truthy non-string.
 */
export function timelineSendOutcomeLabel(v: unknown): string {
  return typeof v === 'string' && Object.hasOwn(TIMELINE_SEND_OUTCOME_LABELS, v)
    ? TIMELINE_SEND_OUTCOME_LABELS[v as SendOutcome]
    : TIMELINE_SEND_OUTCOME_LABELS.unknown;
}

/** The `statuses` block served on GET /api/service-descriptors (G2/G3). */
export function clientStatusRegistry() {
  return {
    booking: { values: BOOKING_STATUSES, labels: BOOKING_STATUS_LABELS },
    payment: { values: PAYMENT_STATUSES, labels: PAYMENT_STATUS_LABELS },
    inquiry: { values: INQUIRY_STATUSES, labels: INQUIRY_STATUS_LABELS },
    send: { values: SEND_STATUSES, labels: SEND_STATUS_LABELS },
    channels: { values: CHANNELS, labels: CHANNEL_LABELS },
    bookingTypes: BOOKING_TYPE_LABELS,
  };
}
